import { Accordion, Box, FormControl, FormLabel, Text, useToast } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { MESSAGE } from "@equidefi/constants/forms";
import UploadArea from "@equidefi/portals/components/accreditation/UploadArea";
import { UploadVaultUrlComponent } from "@equidefi/portals/components/accreditation/UploadComponent";
import { SmartFormProvider } from "@equidefi/portals/components/smart-form";
import { useSavedProfileFiles } from "@equidefi/portals/hooks/useSavedProfiles";
import type { DTO } from "@equidefi/shared/types/dto";

import { SavedProfileFormContainer } from "./SavedProfileFormContainer";

const schema = z.object({
  annual_income: z.string().trim().min(1, MESSAGE.REQUIRED("Annual Income")),
  net_worth: z.string().trim().min(1, MESSAGE.REQUIRED("Net Worth")),
  accredited: z.boolean(),
  broker_dealer: z.boolean(),
});

type AccreditationFormValues = z.infer<typeof schema>;

type AccreditationFormNormalized = {
  annual_income: string;
  net_worth: string;
  accredited: boolean;
  broker_dealer: boolean;
};

type AccreditationFormProps = {
  profile: DTO.SavedProfile;
  onSave: (values: AccreditationFormNormalized) => Promise<void>;
};

const booleanOptions = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const brokerDealerOptions = [
  { value: false, label: "No" },
  { value: true, label: "Yes" },
];

const getInitialValues = (profile: DTO.SavedProfile): AccreditationFormValues => ({
  annual_income: profile.annual_income ?? "",
  net_worth: profile.net_worth ?? "",
  accredited: !!profile.accredited,
  broker_dealer: !!profile.broker_dealer,
});

const normalizeValues = (values: AccreditationFormValues): AccreditationFormNormalized => ({
  annual_income: values.annual_income.trim(),
  net_worth: values.net_worth.trim(),
  accredited: values.accredited,
  broker_dealer: values.broker_dealer,
});

export function AccreditationForm({ profile, onSave }: AccreditationFormProps) {
  const toast = useToast();

  const vaultId = profile.accreditation_files?.[0]?.vault_id ?? null;

  const { data: accreditationFiles = [], refetch: refetchFiles } = useSavedProfileFiles(profile.id ?? "");

  const form = useForm<AccreditationFormValues>({
    defaultValues: getInitialValues(profile),
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const onSubmit = useCallback(
    async (values: AccreditationFormValues) => {
      try {
        await onSave(normalizeValues(values));
      } catch (error) {
        console.error(error);
        toast({
          status: "error",
          description: "There was an error saving accreditation information.",
        });
      }
    },
    [onSave, toast],
  );

  return (
    <SavedProfileFormContainer
      profile={profile}
      title="Accreditation"
      onSave={form.handleSubmit(onSubmit)}
      isLoading={form.formState.isSubmitting}
      isDisabled={!form.formState.isValid}>
      <SmartFormProvider form={form}>
        {({ Input, RadioGroup }) => (
          <>
            <Input name="annual_income" label="Annual Income" isRequired />

            <Input name="net_worth" label="Net Worth" isRequired />

            <FormControl>
              <FormLabel fontSize="lg" fontWeight="700">
                Are you an accredited investor?
              </FormLabel>
              <RadioGroup
                name="accredited"
                direction="wrap"
                elementWidth="48%"
                spacing={2.5}
                mb="4"
                options={booleanOptions}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="lg" fontWeight="700">
                Are you affiliated with a broker-dealer?
              </FormLabel>
              <Text mb="4">In other words, do you or a relative work for a broker-dealer?</Text>
              <RadioGroup
                name="broker_dealer"
                direction="wrap"
                elementWidth="48%"
                spacing={2.5}
                mb="4"
                options={brokerDealerOptions}
              />
              <Text mt="3" fontSize="sm" color="gray.500">
                Please see{" "}
                <Text as="span" color="blue.500" cursor="pointer">
                  What Is An Affiliate of a Broker-Dealer
                </Text>{" "}
                to make sure you make the right selection.
              </Text>
            </FormControl>
          </>
        )}
      </SmartFormProvider>

      {vaultId && (
        <Box
          bg="white"
          borderRadius="lg"
          p="4"
          boxShadow="0 1px 2px rgba(0,0,0,0.2)"
          border="1px solid"
          borderColor="gray.100">
          <Text fontSize="lg" fontWeight="700" mb="2">
            Address Verification Document
          </Text>
          <UploadVaultUrlComponent
            field="utility_bill"
            fieldName="Proof of Address"
            vault={{ id: vaultId, utility_bill: profile.utility_bill } as DTO.Vault}
            savedProfileId={profile.id}
          />
        </Box>
      )}

      <Box>
        <Text fontSize="lg" fontWeight="700" mb="2">
          Accreditation Verification Documents
        </Text>
        {vaultId ? (
          <Accordion variant="card" defaultIndex={-1}>
            <UploadArea
              vault={{ id: vaultId } as DTO.Vault}
              investment={{ id: vaultId } as DTO.Investment}
              accreditationFiles={accreditationFiles}
              refetchFiles={refetchFiles}
              savedProfileId={profile.id}
            />
          </Accordion>
        ) : (
          <Text color="gray.500">No accreditation history to display.</Text>
        )}
      </Box>
    </SavedProfileFormContainer>
  );
}
